import React, { useEffect, useId } from 'react';
import Lottie from 'lottie-react';
import { useRouter } from 'next/router';
import { BehaviorSettings } from '../../../../types';
import { generateClassNameByBehaviorSettings } from '../../../../utils/behaviorSettings';
import { getImpact } from '../../../../utils/donationForm';
import { Field, Form, RadiosButton, TextField } from '../../../01_atoms/FormElements';
import Button from '../../../01_atoms/Button';
import FormattedText from '../../../01_atoms/FormattedText';
import { parseOnlyDigits } from '../../../../utils/formFieldParsers';
import { currencies, getSymbol } from '../../../../utils/currency';
import ToggleCheckboxField from '../../../01_atoms/FormElements/ToggleCheckboxField';
import RangeField from '../../../01_atoms/FormElements/RangeField';
import { pushViewItem as gtmPushViewItem } from '../../../../utils/gtm';
import EyeOffIcon from '../../../../public/static/icons/eye-off.svg';
import EyeOnIcon from '../../../../public/static/icons/eye-on.svg';
import calculatorInitialStateIcon from '../../../../public/static/icons/animations/last-hours-calculator-initial.json';
import calculatorClapsStateIcon from '../../../../public/static/icons/animations/last-hours-calculator-claps.json';
import calculatorHeartStateIcon from '../../../../public/static/icons/animations/last-hours-calculator-heart.json';
import calculatorThumbsUpStateIcon from '../../../../public/static/icons/animations/last-hours-calculator-thumbs-up.json';

import styles from './index.module.scss';

type Impact = {
  amount: number;
  text?: string;
  image?: {
    url: string;
    alt: string;
  };
};

// TODO: Provide the type for the form values.
//  Looks tricky because a few props have dynamic names.
/* eslint-disable-next-line @typescript-eslint/no-explicit-any */
type FormValues = any;

type Props = {
  onSubmit: (values: FormValues) => void;
  calculateHourRate: (amount: number, isDoubled: boolean) => number;
  title: string;
  singleAppeal: {
    appeal_id: number;
    appeal_title: string;
  };
  monthlyAppeal: {
    appeal_id: number;
    appeal_title: string;
  };
  currency: 'GBP' | 'EUR';
  hasMonthlyAppeal: boolean;
  amountRangeStep?: number;
  amountRangeMin?: number;
  amountRangeMax?: number;
  impactsSingleDonation?: Impact[];
  impactsMonthlyDonation?: Impact[];
  behaviorSettings?: BehaviorSettings;
  uuid?: string;
};

type DonationType = 'single_donation' | 'recurring_donation';
type DonationTypes = Record<DonationType, number>;

const getIcon = (state: string) => {
  if (state === 'claps') {
    return calculatorClapsStateIcon;
  } else if (state === 'heart') {
    return calculatorHeartStateIcon;
  } else if (state === 'thumbs-up') {
    return calculatorThumbsUpStateIcon;
  }

  return calculatorInitialStateIcon;
};

const BBLastHoursPay = ({
  title,
  singleAppeal,
  currency,
  impactsSingleDonation = [],
  impactsMonthlyDonation = [],
  hasMonthlyAppeal,
  onSubmit,
  calculateHourRate,
  behaviorSettings,
  amountRangeStep = 2000,
  amountRangeMin = 18000,
  amountRangeMax = 120000,
  uuid,
}: Props) => {
  const formId = useId();
  const router = useRouter();

  // To have multiple form instances on the same page,
  // field names must be unique to not interfere with each other.
  const donationTypeName = `${formId}_donation_type`;
  const amountName = `${formId}_amount`;
  const visibleAmountName = `${formId}_visible_amount`;
  const doubledName = `${formId}_doubled`;
  const iconName = `${formId}_icon`;
  const visibleName = `${formId}_visible`;
  const amountEnteredName = `${formId}_amount_entered`;
  const queryName = `${formId}_query`;

  const initialValues = {
    formId: formId,
    [donationTypeName]: 'single_donation',
    [amountEnteredName]: false,
    [iconName]: 'initial',
    [doubledName]: false,
    [visibleName]: true,
    [amountName]: {
      single_donation: 0,
      recurring_donation: 0,
    },
    [visibleAmountName]: {
      single_donation: 0,
      recurring_donation: 0,
    },
    [queryName]: router.query,
  };

  const donationTypeOptions = [
    { value: 'single_donation', label: 'One-off' },
    { value: 'recurring_donation', label: 'Monthly' },
  ];

  // Pushing view item event to GTM.
  useEffect(() => {
    if (singleAppeal.appeal_id && singleAppeal.appeal_title) {
      gtmPushViewItem([
        {
          item_id: singleAppeal.appeal_id,
          item_name: singleAppeal.appeal_title,
          item_category: 'lastHourPay',
          item_variant: initialValues[donationTypeName],
        },
      ]);
    }
  }, [initialValues[donationTypeName]]);

  async function onFormSubmit(values: FormValues) {
    await onSubmit(values);
  }

  const getCurrentImpact = (values: FormValues) => {
    if (!values) {
      return null;
    }

    if (!getAmount(values)) {
      return 'See how your one hour can make a difference, start with our calculator.';
    }

    const hourlyRate = getHourlyRate(values);

    let currentImpact;
    if (values[donationTypeName] === 'single_donation' && values[amountName].single_donation) {
      currentImpact = getImpact(hourlyRate, impactsSingleDonation);
    }

    if (
      values[donationTypeName] === 'recurring_donation' &&
      values[amountName].recurring_donation
    ) {
      currentImpact = getImpact(hourlyRate, impactsMonthlyDonation);
    }

    if (currentImpact) {
      return currentImpact.text;
    }

    return '';
  };

  // Dispatch "input" event for "range" slider.
  const dispatchRangeElementInput = (e: React.ChangeEvent<HTMLInputElement>, amount: number) => {
    const form = e.target.closest('form');

    if (form) {
      const rangeElement = form.querySelector('input[type="range"]') as HTMLInputElement;
      if (rangeElement) {
        rangeElement.style.setProperty('--value', String(amount));
      }
    }
  };

  const FormElement = () => {
    return (
      <Form
        onSubmit={onFormSubmit}
        initialValues={initialValues}
        mutators={{
          // Create a mutator which allows to modify field's value by external callbacks.
          setValue: ([field, value], state, { changeValue }) => {
            changeValue(state, field, () => value);
          },
        }}
        subscription={{ submitting: true, values: true }}
        render={({ handleSubmit, submitting, values, form: { mutators } }) => {
          const onChangeAmount = (e: React.ChangeEvent<HTMLInputElement>) => {
            const digitValue = e.target.value.replaceAll('⁎', '');

            // Check the entered symbol and return right here if it isn't digit.
            if (digitValue.length && isNaN(Number(digitValue[digitValue.length - 1]))) {
              return;
            }

            let amount = parseInt(digitValue, 10);

            if (!values[visibleName] && e.target.type !== 'range') {
              const realAmount = values[amountName][values[donationTypeName]];
              if (!isNaN(amount)) {
                amount = +(String(realAmount) + String(amount));
              } else if (realAmount >= 10) {
                amount = +String(realAmount).slice(0, -1);
              } else {
                amount = 0;
              }
            }

            if (!amount) {
              amount = 0;
            }

            refreshIcon(amount, values[donationTypeName], values[doubledName]);

            mutators.setValue(`${amountName}.${values[donationTypeName]}`, amount);

            if (!values[visibleName]) {
              mutators.setValue(
                `${visibleAmountName}.${values[donationTypeName]}`,
                '⁎'.repeat(String(amount).length),
              );
            } else {
              mutators.setValue(`${visibleAmountName}.${values[donationTypeName]}`, amount);
            }

            // Dispatch "input" event for "range" slider.
            dispatchRangeElementInput(e, amount);
          };

          const refreshIcon = (currentAmount: number, donationType: string, isDoubled: boolean) => {
            let iconValue = 'initial';

            if (currentAmount) {
              if (donationType === 'recurring_donation') {
                iconValue = 'heart';
              } else if (
                values[amountEnteredName] &&
                (isDoubled || currentAmount > getAmount(values))
              ) {
                iconValue = 'claps';
              } else {
                iconValue = 'thumbs-up';
              }
            }

            mutators.setValue(iconName, iconValue);
          };

          const hideAmount = () => {
            mutators.setValue(visibleName, false);
            mutators.setValue(
              `${visibleAmountName}.single_donation`,
              '⁎'.repeat(String(values[amountName].single_donation).length),
            );
            mutators.setValue(
              `${visibleAmountName}.recurring_donation`,
              '⁎'.repeat(String(values[amountName].recurring_donation).length),
            );
          };

          const unhideAmount = () => {
            mutators.setValue(visibleName, true);
            mutators.setValue(
              `${visibleAmountName}.single_donation`,
              values[amountName].single_donation,
            );
            mutators.setValue(
              `${visibleAmountName}.recurring_donation`,
              values[amountName].recurring_donation,
            );
          };

          const onChangeDonationType = (e: React.ChangeEvent<HTMLInputElement>) => {
            mutators.setValue(donationTypeName, e.target.value);

            const newValues = { ...values, [donationTypeName]: e.target.value };

            if (e.target.value === 'single_donation') {
              mutators.setValue(
                `${amountName}.single_donation`,
                values[amountName].recurring_donation,
              );
              mutators.setValue(
                `${visibleAmountName}.single_donation`,
                values[visibleName]
                  ? values[amountName].recurring_donation
                  : '⁎'.repeat(String(values[amountName].recurring_donation).length),
              );

              newValues[amountName].single_donation = values[amountName].recurring_donation;
            } else if (e.target.value === 'recurring_donation') {
              mutators.setValue(
                `${amountName}.recurring_donation`,
                values[amountName].single_donation,
              );
              mutators.setValue(
                `${visibleAmountName}.recurring_donation`,
                values[visibleName]
                  ? values[amountName].single_donation
                  : '⁎'.repeat(String(values[amountName].single_donation).length),
              );

              newValues[amountName].recurring_donation = values[amountName].single_donation;
            }

            refreshIcon(getAmount(values), e.target.value, values[doubledName]);

            // Dispatch "input" event for "range" slider.
            dispatchRangeElementInput(e, getAmount(newValues));
          };

          return (
            <>
              <div className={styles['bb-last-hours-pay__form']}>
                <form onSubmit={handleSubmit}>
                  <div className={styles['bb-last-hours-pay__salary']}>
                    <div
                      className={styles['bb-last-hours-pay__salary-title']}
                      id={`${formId}-salary-amount-title`}
                    >
                      What is your gross annual salary?
                    </div>
                    <div className={styles['bb-last-hours-pay__salary-info']}>
                      This information is not stored or shared when using this calculator.
                    </div>
                    <div
                      className={`${styles['bb-last-hours-pay__amount']} ${!values[visibleName] ? styles['bb-last-hours-pay__amount--hidden'] : ''}`}
                    >
                      <Field
                        name={`${visibleAmountName}.${values[donationTypeName]}`}
                        type="text"
                        parse={parseOnlyDigits}
                      >
                        {() => (
                          <>
                            <TextField
                              name={`${visibleAmountName}.${values[donationTypeName]}`}
                              // id and onChange props are not defined in react-final-form type.
                              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                              // @ts-ignore
                              id={visibleAmountName}
                              onChange={onChangeAmount}
                              onBlur={(e: React.ChangeEvent<HTMLInputElement>) => {
                                // Set "amount entered" flag only if positive
                                // amount actually was entered.
                                if (+e.target.value) {
                                  mutators.setValue(amountEnteredName, true);
                                }
                              }}
                              icon={
                                <span className={styles['bb-last-hours-pay__amount-icon']}>
                                  {currencies[currency]}
                                </span>
                              }
                              label=""
                              // Enable numeric keyboard for mobile devices.
                              inputMode="numeric"
                              pattern="[0-9]+"
                              infoOnPatternNotMatched="Please use numbers only"
                              aria-label={getSymbol(currency)}
                              maxLength={9}
                            />
                            <button
                              onClick={(e) => {
                                e.preventDefault();
                                if (values[visibleName]) {
                                  hideAmount();
                                } else {
                                  unhideAmount();
                                }
                              }}
                              aria-label={values[visibleName] ? 'Hide amount' : 'Display amount'}
                            >
                              {values[visibleName] ? <EyeOffIcon /> : <EyeOnIcon />}
                            </button>
                          </>
                        )}
                      </Field>
                    </div>
                    <div className={styles['bb-last-hours-pay__range']}>
                      <Field name={`${amountName}.${values[donationTypeName]}`} type="range">
                        {() => (
                          <RangeField
                            name={`${amountName}.${values[donationTypeName]}`}
                            // onChange prop is not defined in react-final-form type.
                            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                            // @ts-ignore
                            onChange={(ev) => {
                              onChangeAmount(ev);
                              mutators.setValue(amountEnteredName, true);
                            }}
                            aria-labelledby={`${formId}-salary-amount-title`}
                            min={amountRangeMin}
                            max={amountRangeMax}
                            step={amountRangeStep}
                          />
                        )}
                      </Field>
                    </div>
                  </div>
                  <div className={styles['bb-last-hours-pay__hourly-rate']}>
                    <div className={styles['bb-last-hours-pay__hourly-rate-title']}>
                      One hour of your salary is
                    </div>
                    <div className={styles['bb-last-hours-pay__hourly-rate-info']}>
                      <div className={styles['bb-last-hours-pay__hourly-rate-amount']}>
                        {getSymbol(currency)} {getHourlyRate(values)}
                      </div>
                      <div className={styles['bb-last-hours-pay__hourly-rate-double']}>
                        <Field name={`${doubledName}`} type="checkbox">
                          {() => (
                            <ToggleCheckboxField
                              name={doubledName}
                              // onChange prop is not defined in react-final-form type.
                              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                              // @ts-ignore
                              onChange={() => {
                                mutators.setValue(doubledName, !values[doubledName]);
                                refreshIcon(
                                  getAmount(values),
                                  values[donationTypeName],
                                  !values[doubledName],
                                );
                              }}
                              disabled={!getAmount(values)}
                              label={'Double it'}
                              aria-label={'Double it'}
                            />
                          )}
                        </Field>
                      </div>
                    </div>
                    {!hasMonthlyAppeal && (
                      <div className={styles['bb-last-hours-pay__cta']}>
                        <Button
                          className={styles['bb-last-hours-pay__submit']}
                          type={'primary'}
                          size="extra-large"
                          isLoading={submitting}
                          isDisabled={!getAmount(values)}
                          tabIndex={0}
                          withArrow={true}
                          isBlock
                        >
                          Donate{' '}
                          {getAmount(values) !== 0
                            ? `${getSymbol(currency)}${getHourlyRate(values)}`
                            : ''}
                        </Button>
                      </div>
                    )}
                  </div>
                  {hasMonthlyAppeal && (
                    <div className={styles['bb-last-hours-pay__bottom']}>
                      <div className={styles['bb-last-hours-pay__bottom-title']}>
                        How often would you like to give?
                      </div>

                      <div
                        className={`${styles['bb-last-hours-pay__donation-type']} ${!getAmount(values) ? styles['bb-last-hours-pay__donation-type--disabled'] : styles['bb-last-hours-pay__donation-type--enabled']}`}
                      >
                        <RadiosButton
                          name={donationTypeName}
                          size="large"
                          mutateFormValue={mutators.setValue}
                          options={donationTypeOptions}
                          styling={'default'}
                          // onChange prop is not defined in react-final-form type.
                          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                          // @ts-ignore
                          onChange={onChangeDonationType}
                          disabled={!getAmount(values)}
                        />
                      </div>

                      {getCurrentImpact(values) && (
                        <div className={styles['bb-last-hours-pay__impact-mobile']}>
                          <div
                            className={styles['bb-last-hours-pay__impact-icon']}
                            data-component={`impact-icon-${values[iconName]}`}
                          >
                            <Lottie animationData={getIcon(values[iconName])} loop={true} />
                          </div>
                          <div className={styles['bb-last-hours-pay__impact-text']}>
                            <FormattedText
                              className={styles['bb-hero-with-donation-widget__impact-text']}
                              text={getCurrentImpact(values)}
                            />
                          </div>
                        </div>
                      )}
                      <div className={styles['bb-last-hours-pay__cta']}>
                        <Button
                          className={styles['bb-last-hours-pay__submit']}
                          type={'primary'}
                          size="extra-large"
                          isLoading={submitting}
                          isDisabled={!getAmount(values)}
                          tabIndex={0}
                          withArrow={true}
                          isBlock
                        >
                          Donate{' '}
                          {getAmount(values) !== 0
                            ? `${getSymbol(currency)}${getHourlyRate(values)}`
                            : ''}
                        </Button>
                      </div>
                    </div>
                  )}
                </form>
              </div>
              <div className={styles['bb-last-hours-pay__impact']}>
                <div>
                  <div
                    className={styles['bb-last-hours-pay__impact-icon']}
                    data-component={`impact-icon-${values[iconName]}`}
                  >
                    <Lottie animationData={getIcon(values[iconName])} loop={true} />
                  </div>
                </div>
                <div className={styles['bb-last-hours-pay__impact-text']}>
                  {getCurrentImpact(values) && (
                    <FormattedText
                      className={styles['bb-hero-with-donation-widget__impact-text']}
                      text={getCurrentImpact(values)}
                    />
                  )}
                </div>
              </div>
            </>
          );
        }}
      />
    );
  };

  const getAmount = (values: typeof initialValues) => {
    const donationType = values[donationTypeName] as DonationType;
    const salary = values[amountName] as DonationTypes;
    return salary[donationType];
  };

  const getHourlyRate = (values: typeof initialValues) => {
    return calculateHourRate(getAmount(values), values[doubledName] as boolean);
  };

  return (
    <div
      className={`${styles['bb-last-hours-pay']} ${generateClassNameByBehaviorSettings(behaviorSettings)}`}
      data-component={'bb-last-hours-pay'}
      id={uuid}
    >
      <div className={styles['bb-last-hours-pay__root']}>
        <div className="container">
          <div className={styles['bb-last-hours-pay__heading']}>{title}</div>
          <div className={styles['bb-last-hours-pay__wrapper']}>
            <FormElement />
          </div>
        </div>
      </div>
    </div>
  );
};

export default BBLastHoursPay;
